import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getGradeLabel } from '@car-pool/shared/schoolRecords';
import {
  isDefaultCarpoolLine,
  parseCarpoolLineGradeSplitIds,
  parseCarpoolLineGrades,
} from '@car-pool/shared/carpoolLines';
import { apiFetch } from '../config/api';
import { describeWindow, findScheduleConflicts } from '../lib/lineSchedule';
import SchoolLayout from '../components/SchoolLayout';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const CAMPUS_TYPES = ['Lower', 'Middle', 'High', 'K-8', 'Other'];
const CAMPUS_TIME_ZONES = ['Eastern', 'Central', 'Mountain', 'Pacific', 'Alaska', 'Hawaii'];
const CARPOOL_LINE_TYPES = ['parent', 'bus', 'rideshare', 'staff', 'other'];
const FALLBACK_GRADE_CODES = [-3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const STUDENT_GROUPING_MODES = new Set(['grades', 'grades_and_splits', 'splits_only']);
let googleMapsPromise = null;

function normalizeStudentGroupingMode(value) {
  return STUDENT_GROUPING_MODES.has(value) ? value : 'grades';
}

export function getPickupLineAssignmentVisibility(groupingMode) {
  const mode = normalizeStudentGroupingMode(groupingMode);
  return {
    showGrades: mode !== 'splits_only',
    showGradeSplits: mode !== 'grades',
  };
}

export function getPickupLineAssignmentsForSave({ groupingMode, assignedGrades, assignedGradeSplitIds }) {
  const mode = normalizeStudentGroupingMode(groupingMode);
  return {
    assignedGrades: mode === 'splits_only' ? [] : assignedGrades,
    assignedGradeSplitIds: mode === 'grades' ? [] : assignedGradeSplitIds,
  };
}

function isActive(value) {
  return value === true || value === 1 || String(value) === '1';
}

function normalizeCampusTimeZone(value) {
  return CAMPUS_TIME_ZONES.includes(value) ? value : 'Eastern';
}

function buildCampusForm(school = null) {
  return {
    name: school?.school_nickname ? `${school.school_nickname} Main Campus` : 'Main Campus',
    type: 'Other',
    street_address: school?.street_address || '',
    city: school?.city || '',
    state: school?.state || '',
    zip_code: school?.zip_code || '',
    time_zone: normalizeCampusTimeZone(school?.time_zone),
    is_dst: Boolean(school?.is_dst),
    main_phone: school?.main_phone || '',
    main_email: school?.main_email || '',
  };
}

function parseZonePoints(points) {
  if (!points) return [];

  if (Array.isArray(points)) {
    return points
      .map((point) => ({
        lat: Number(point?.lat),
        lng: Number(point?.lng),
      }))
      .filter((point) => !Number.isNaN(point.lat) && !Number.isNaN(point.lng));
  }

  if (typeof points === 'string') {
    try {
      return parseZonePoints(JSON.parse(points));
    } catch {
      return [];
    }
  }

  return [];
}

function buildPolygonPoints(points) {
  if (points.length < 2) return '';

  const latitudes = points.map((point) => point.lat);
  const longitudes = points.map((point) => point.lng);

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  const latSpan = maxLat - minLat || 1;
  const lngSpan = maxLng - minLng || 1;
  const padding = 14;

  return points.map((point) => {
    const x = padding + ((point.lng - minLng) / lngSpan) * (100 - padding * 2);
    const y = padding + ((maxLat - point.lat) / latSpan) * (100 - padding * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
}

function formatLineType(lineType) {
  return String(lineType || 'pickup').replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function buildLineForm(campuses = []) {
  return {
    campus_id: campuses[0]?.campus_id ? String(campuses[0].campus_id) : '',
    name: '',
    line_type: 'parent',
    dismissal_time: '',
    dismissal_time_overrides_defaults: false,
    active_duration_minutes: '',
    assigned_grades: [],
    assigned_grade_split_ids: [],
    is_default: false,
    notes: '',
  };
}

function toggleGradeSelection(grades, gradeCode) {
  const normalized = Number(gradeCode);
  const current = new Set((grades || []).map(Number));
  if (current.has(normalized)) {
    current.delete(normalized);
  } else {
    current.add(normalized);
  }
  return [...current].sort((left, right) => left - right);
}

function formatAssignedGrades(line, gradeLabelMap = {}) {
  const grades = parseCarpoolLineGrades(line);
  if (!grades.length) return 'No grade fallback';
  return grades.map((grade) => getGradeLabel(grade, gradeLabelMap, { short: true })).join(', ');
}

function formatAssignedGradeSplits(line, gradeSplitById = {}, gradeLabelMap = {}, includeGrade = false) {
  const splitIds = parseCarpoolLineGradeSplitIds(line);
  if (!splitIds.length) return 'No split fallback';

  return splitIds.map((splitId) => {
    const split = gradeSplitById[splitId];
    if (!split) return `Split #${splitId}`;
    if (!includeGrade) return split.split_code;
    return `${getGradeLabel(split.grade, gradeLabelMap, { short: true })} - ${split.split_code}`;
  }).join(', ');
}

function toTimeInputValue(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function toApiTimeValue(inputValue) {
  if (!inputValue) return null;
  const match = String(inputValue).match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}:00`;
}

function formatPickupTime(value) {
  if (!value) return 'Not set';

  const [hours = '0', minutes = '0'] = String(value).split(':');
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in a browser.'));
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured.'));
  }

  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('Google Maps failed to load.')));
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Google Maps failed to load.'));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

function getCampusAddress(campus) {
  return [
    campus?.street_address,
    campus?.city,
    campus?.state,
    campus?.zip_code,
  ].filter(Boolean).join(', ');
}

function getPointCenter(points) {
  if (!points.length) return null;
  const total = points.reduce((sum, point) => ({
    lat: sum.lat + point.lat,
    lng: sum.lng + point.lng,
  }), { lat: 0, lng: 0 });

  return {
    lat: total.lat / points.length,
    lng: total.lng / points.length,
  };
}

function readPolygonPath(polygon) {
  return polygon.getPath().getArray().map((latLng) => ({
    lat: Number(latLng.lat().toFixed(7)),
    lng: Number(latLng.lng().toFixed(7)),
  }));
}

async function geocodeCampusCenter(apiKey, campus) {
  const address = getCampusAddress(campus);
  if (!address) {
    throw new Error('Campus address is not configured.');
  }

  const google = await loadGoogleMaps(apiKey);
  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        resolve({
          lat: Number(results[0].geometry.location.lat().toFixed(7)),
          lng: Number(results[0].geometry.location.lng().toFixed(7)),
        });
        return;
      }

      reject(new Error('Could not locate the campus address on the map.'));
    });
  });
}

function buildStarterZone(center) {
  const latOffset = 0.00018;
  const lngOffset = Math.max(0.00018, 0.00018 / Math.cos((center.lat * Math.PI) / 180));

  return [
    { lat: Number((center.lat + latOffset).toFixed(7)), lng: Number((center.lng - lngOffset).toFixed(7)) },
    { lat: Number((center.lat + latOffset).toFixed(7)), lng: Number((center.lng + lngOffset).toFixed(7)) },
    { lat: Number((center.lat - latOffset).toFixed(7)), lng: Number((center.lng + lngOffset).toFixed(7)) },
    { lat: Number((center.lat - latOffset).toFixed(7)), lng: Number((center.lng - lngOffset).toFixed(7)) },
  ];
}

function GoogleZoneEditor({ apiKey, campus, points, onPointsChange }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const geocoderRef = useRef(null);
  const pointsRef = useRef(points);
  const listenersRef = useRef([]);
  const [loadError, setLoadError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !mapEl.current) return;

        const initialCenter = getPointCenter(pointsRef.current) || { lat: 25.7617, lng: -80.1918 };
        mapRef.current = new google.maps.Map(mapEl.current, {
          center: initialCenter,
          zoom: pointsRef.current.length ? 18 : 15,
          mapTypeId: 'roadmap',
          clickableIcons: false,
          streetViewControl: false,
          fullscreenControl: true,
          mapTypeControl: true,
          gestureHandling: 'greedy',
        });

        geocoderRef.current = new google.maps.Geocoder();
        setReady(true);
        setLoadError('');
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });

    return () => {
      cancelled = true;
      listenersRef.current.forEach((listener) => listener.remove());
      listenersRef.current = [];
    };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return undefined;

    const listener = mapRef.current.addListener('click', (event) => {
      if (!event.latLng) return;
      onPointsChange([
        ...pointsRef.current,
        {
          lat: Number(event.latLng.lat().toFixed(7)),
          lng: Number(event.latLng.lng().toFixed(7)),
        },
      ]);
    });

    return () => listener.remove();
  }, [onPointsChange, ready]);

  useEffect(() => {
    if (!ready || !mapRef.current || !window.google?.maps) return;

    const google = window.google;
    listenersRef.current.forEach((listener) => listener.remove());
    listenersRef.current = [];

    if (!polygonRef.current) {
      polygonRef.current = new google.maps.Polygon({
        map: mapRef.current,
        editable: true,
        draggable: true,
        strokeColor: '#16a34a',
        strokeOpacity: 0.95,
        strokeWeight: 3,
        fillColor: '#22c55e',
        fillOpacity: 0.22,
      });
    }

    polygonRef.current.setPath(points.map((point) => new google.maps.LatLng(point.lat, point.lng)));

    const path = polygonRef.current.getPath();
    ['insert_at', 'remove_at', 'set_at'].forEach((eventName) => {
      listenersRef.current.push(path.addListener(eventName, () => {
        onPointsChange(readPolygonPath(polygonRef.current));
      }));
    });
    listenersRef.current.push(polygonRef.current.addListener('dragend', () => {
      onPointsChange(readPolygonPath(polygonRef.current));
    }));

    if (points.length >= 2) {
      const bounds = new google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend(point));
      mapRef.current.fitBounds(bounds, 64);
    }
  }, [onPointsChange, points, ready]);

  useEffect(() => {
    if (!ready || points.length > 0 || !geocoderRef.current || !mapRef.current) return;

    const address = getCampusAddress(campus);
    if (!address) return;

    geocoderRef.current.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location && mapRef.current) {
        mapRef.current.setCenter(results[0].geometry.location);
        mapRef.current.setZoom(17);
      }
    });
  }, [campus, points.length, ready]);

  if (loadError) {
    return (
      <div className="spz-google-map-error">
        {loadError}
      </div>
    );
  }

  return <div ref={mapEl} className="spz-google-map" />;
}

function formatTimestamp(value) {
  if (!value) return 'Needs review';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Needs review';

  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getLineStatus(line, zone) {
  if (!line?.is_active || !line?.geofenced_enabled) {
    return { label: 'Inactive', tone: 'inactive' };
  }

  if (!zone) {
    return { label: 'Setup Required', tone: 'warning' };
  }

  return { label: 'Active', tone: 'active' };
}

export function comparePickupZoneLines(left, right, zoneByLineId = {}) {
  const defaultDiff = Number(isDefaultCarpoolLine(right)) - Number(isDefaultCarpoolLine(left));
  if (defaultDiff !== 0) return defaultDiff;

  const leftStatus = getLineStatus(left, zoneByLineId[left.carpool_line_id]);
  const rightStatus = getLineStatus(right, zoneByLineId[right.carpool_line_id]);
  const rank = { active: 0, warning: 1, inactive: 2 };
  const toneDiff = rank[leftStatus.tone] - rank[rightStatus.tone];
  if (toneDiff !== 0) return toneDiff;

  return String(left.name || '').localeCompare(String(right.name || ''));
}

function ZonePreview({ points, large = false }) {
  const polygonPoints = buildPolygonPoints(points);

  return (
    <div className={`spz-map-card ${large ? 'large' : ''}`}>
      <svg viewBox="0 0 100 100" className="spz-map-svg" role="img" aria-label="Pickup zone preview">
        <rect x="0" y="0" width="100" height="100" rx="8" fill="#eef2f6" />
        <path d="M5 20 L95 26" className="spz-road" />
        <path d="M0 63 L100 58" className="spz-road" />
        <path d="M16 6 L22 94" className="spz-road" />
        <path d="M72 0 L78 100" className="spz-road" />
        <path d="M33 15 L59 12 L62 39 L38 44 Z" className="spz-building" />
        <path d="M47 53 L69 50 L72 83 L44 86 Z" className="spz-building" />
        <path d="M18 37 L31 35 L33 53 L16 55 Z" className="spz-building" />
        {polygonPoints ? (
          <>
            <polygon points={polygonPoints} className="spz-zone-fill" />
            <polyline points={polygonPoints} className="spz-zone-line" />
            {polygonPoints.split(' ').map((point, index) => {
              const [cx, cy] = point.split(',');
              return <circle key={`${point}-${index}`} cx={cx} cy={cy} r="1.6" className="spz-zone-node" />;
            })}
          </>
        ) : null}
      </svg>
      {!polygonPoints ? <div className="spz-map-empty">No saved boundary yet</div> : null}
    </div>
  );
}

export default function PickupZonesPage({ token, navigate, schoolId, user, bare = false, focusBuilder = false }) {
  const [school, setSchool] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [carpoolLines, setCarpoolLines] = useState([]);
  const [gradeNames, setGradeNames] = useState([]);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [draftPoints, setDraftPoints] = useState([]);
  const [savingZone, setSavingZone] = useState(false);
  const [creatingLine, setCreatingLine] = useState(false);
  const [campusFormOpen, setCampusFormOpen] = useState(false);
  const [lineFormOpen, setLineFormOpen] = useState(false);
  const [campusSaving, setCampusSaving] = useState(false);
  const [campusForm, setCampusForm] = useState(() => buildCampusForm());
  const [lineForm, setLineForm] = useState(() => buildLineForm());
  const [lineDismissalTime, setLineDismissalTime] = useState('');
  const [lineAbsoluteDismissalTime, setLineAbsoluteDismissalTime] = useState(false);
  const [lineActiveDuration, setLineActiveDuration] = useState('');
  const [lineAssignedGrades, setLineAssignedGrades] = useState([]);
  const [lineAssignedGradeSplitIds, setLineAssignedGradeSplitIds] = useState([]);
  const [lineIsDefault, setLineIsDefault] = useState(false);
  const [savingLineSettings, setSavingLineSettings] = useState(false);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingSeedLineId, setPendingSeedLineId] = useState(null);
  const [pendingStarterSetup, setPendingStarterSetup] = useState(false);
  const editorCardRef = useRef(null);
  const builderInitializedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [schoolRes, campusesRes, linesRes, zonesRes, gradeNamesRes, gradeSplitsRes] = await Promise.all([
        apiFetch(`/schools/${schoolId}`, {}, token).catch(() => ({ data: null })),
        apiFetch(`/campuses/school/${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch('/carpool-lines', {}, token).catch(() => ({ data: [] })),
        apiFetch('/geofenced-zones', {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token).catch(() => ({ data: [] })),
        apiFetch(`/school-grade-splits?school_id=${schoolId}&is_active=true`, {}, token).catch(() => ({ data: [] })),
      ]);

      setSchool(schoolRes.data || null);
      setCampuses(campusesRes.data || []);
      setCarpoolLines(linesRes.data || []);
      setZones(zonesRes.data || []);
      setGradeNames((gradeNamesRes.data || []).filter((grade) => grade.is_active));
      setGradeSplits((gradeSplitsRes.data || [])
        .filter((split) => isActive(split.is_active))
        .sort((left, right) => (
          Number(left.grade) - Number(right.grade)
          || String(left.split_code || '').localeCompare(String(right.split_code || ''))
        )));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const campusById = useMemo(
    () => Object.fromEntries(campuses.map((campus) => [campus.campus_id, campus])),
    [campuses]
  );

  const campusIds = useMemo(
    () => new Set(campuses.map((campus) => campus.campus_id)),
    [campuses]
  );

  const zoneByLineId = useMemo(
    () => Object.fromEntries(zones.map((zone) => [zone.carpool_line_id, zone])),
    [zones]
  );

  const gradeLabelMap = useMemo(() => {
    const entries = gradeNames.map((grade) => [Number(grade.grade_code), grade.label]);
    return Object.fromEntries(entries);
  }, [gradeNames]);

  const gradeSplitById = useMemo(
    () => Object.fromEntries(gradeSplits.map((split) => [Number(split.id), split])),
    [gradeSplits]
  );

  const studentGroupingMode = normalizeStudentGroupingMode(school?.student_grouping_mode);
  const assignmentVisibility = getPickupLineAssignmentVisibility(studentGroupingMode);
  const showGradeAssignments = assignmentVisibility.showGrades;
  const showGradeSplitAssignments = assignmentVisibility.showGradeSplits;

  const gradeOptions = useMemo(() => {
    const configured = gradeNames
      .map((grade) => Number(grade.grade_code))
      .filter((grade) => Number.isInteger(grade));
    const gradeCodes = configured.length ? configured : FALLBACK_GRADE_CODES;

    return [...new Set(gradeCodes)]
      .sort((left, right) => left - right)
      .map((gradeCode) => ({
        gradeCode,
        label: getGradeLabel(gradeCode, gradeLabelMap, { short: true }),
      }));
  }, [gradeLabelMap, gradeNames]);

  const gradeSplitOptions = useMemo(() => gradeSplits.map((split) => ({
    splitId: Number(split.id),
    label: studentGroupingMode === 'splits_only'
      ? split.split_code
      : `${getGradeLabel(split.grade, gradeLabelMap, { short: true })} - ${split.split_code}`,
  })), [gradeLabelMap, gradeSplits, studentGroupingMode]);

  const schoolLines = useMemo(() => {
    return [...carpoolLines]
      .filter((line) => campusIds.has(line.campus_id))
      .sort((left, right) => comparePickupZoneLines(left, right, zoneByLineId));
  }, [campusIds, carpoolLines, zoneByLineId]);

  useEffect(() => {
    if (!schoolLines.length) {
      setSelectedLineId(null);
      return;
    }

    const exists = schoolLines.some((line) => line.carpool_line_id === selectedLineId);
    if (!exists) {
      setSelectedLineId(schoolLines[0].carpool_line_id);
    }
  }, [schoolLines, selectedLineId]);

  useEffect(() => {
    if (!school) return;
    setCampusForm((current) => {
      if (campuses.length > 0) return current;
      return buildCampusForm(school);
    });
  }, [campuses.length, school]);

  useEffect(() => {
    setLineForm((current) => {
      const campusExists = campuses.some((campus) => String(campus.campus_id) === String(current.campus_id));
      if (campusExists) return current;
      return buildLineForm(campuses);
    });
  }, [campuses]);

  useEffect(() => {
    if (loading || builderInitializedRef.current) return;

    if (campuses.length === 0) {
      setCampusFormOpen(true);
      setLineFormOpen(false);
    } else {
      setCampusFormOpen(false);
      setLineFormOpen(true);
    }

    builderInitializedRef.current = true;
  }, [campuses.length, loading]);

  const selectedLine = useMemo(
    () => schoolLines.find((line) => line.carpool_line_id === selectedLineId) || null,
    [schoolLines, selectedLineId]
  );

  const selectedCampus = selectedLine ? campusById[selectedLine.campus_id] : null;
  const selectedZone = selectedLine ? zoneByLineId[selectedLine.carpool_line_id] : null;
  const selectedPoints = useMemo(
    () => parseZonePoints(selectedZone?.points),
    [selectedZone]
  );
  const selectedStatus = getLineStatus(selectedLine, selectedZone);
  const effectiveDismissalTime = selectedLine?.dismissal_time || school?.default_dismissal_time || null;

  useEffect(() => {
    setDraftPoints(selectedPoints);
    setSuccess('');
  }, [selectedLineId, selectedPoints]);

  useEffect(() => {
    setLineDismissalTime(toTimeInputValue(selectedLine?.dismissal_time));
    setLineAbsoluteDismissalTime(Boolean(Number(selectedLine?.dismissal_time_overrides_defaults)));
    setLineActiveDuration(selectedLine?.active_duration_minutes ? String(selectedLine.active_duration_minutes) : '');
    setLineAssignedGrades(parseCarpoolLineGrades(selectedLine));
    setLineAssignedGradeSplitIds(parseCarpoolLineGradeSplitIds(selectedLine));
    setLineIsDefault(isDefaultCarpoolLine(selectedLine));
  }, [selectedLine]);

  // Which other lines would be live at the same time as the one being edited.
  // Two lines over the same ground both being live is what turned one arrival
  // into two geofence enters.
  const scheduleConflicts = useMemo(() => {
    if (!selectedLine) return [];

    const pending = {
      ...selectedLine,
      dismissal_time: lineDismissalTime || null,
      active_duration_minutes: lineActiveDuration === '' ? null : Number(lineActiveDuration),
    };
    return findScheduleConflicts(pending, schoolLines);
  }, [lineActiveDuration, lineDismissalTime, schoolLines, selectedLine]);

  const activityItems = useMemo(() => {
    const items = schoolLines.flatMap((line) => {
      const campus = campusById[line.campus_id];
      const zone = zoneByLineId[line.carpool_line_id];
      const entries = [];

      if (zone) {
        entries.push({
          id: `zone-${zone.id}`,
          title: 'Geofence updated',
          lineName: line.name,
          detail: `${campus?.name || 'Campus'} adjusted pickup boundary`,
          timestamp: zone.created_at,
        });
      }

      if (line.geofenced_enabled && !zone) {
        entries.push({
          id: `missing-${line.carpool_line_id}`,
          title: 'Zone builder pending',
          lineName: line.name,
          detail: `${campus?.name || 'Campus'} has no saved polygon yet`,
          timestamp: null,
        });
      }

      if (!line.is_active) {
        entries.push({
          id: `inactive-${line.carpool_line_id}`,
          title: 'Zone deactivated',
          lineName: line.name,
          detail: `${campus?.name || 'Campus'} lane is currently inactive`,
          timestamp: null,
        });
      }

      return entries;
    });

    return items.sort((left, right) => String(right.timestamp || '').localeCompare(String(left.timestamp || '')));
  }, [campusById, schoolLines, zoneByLineId]);

  const focusBuilderTarget = useCallback(() => {
    const nextLine = schoolLines.find((line) => !zoneByLineId[line.carpool_line_id] && line.is_active) || schoolLines[0];
    if (nextLine) {
      setSelectedLineId(nextLine.carpool_line_id);
    }
  }, [schoolLines, zoneByLineId]);

  useEffect(() => {
    if (!focusBuilder || loading || !schoolLines.length) return;
    focusBuilderTarget();
  }, [focusBuilder, focusBuilderTarget, loading, schoolLines.length]);

  useEffect(() => {
    if (!pendingSeedLineId || !selectedLine || selectedLine.carpool_line_id !== pendingSeedLineId) return;

    editorCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (selectedZone || draftPoints.length > 0) {
      setPendingSeedLineId(null);
      return;
    }

    let cancelled = false;

    geocodeCampusCenter(GOOGLE_MAPS_API_KEY, selectedCampus)
      .then((center) => {
        if (cancelled) return;
        setDraftPoints(buildStarterZone(center));
        setError('');
        setSuccess('Starter geofence created near the campus address. Drag the corners, then save.');
        setPendingSeedLineId(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not create a starter geofence.');
        setPendingSeedLineId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [draftPoints.length, selectedCampus, selectedLine, selectedZone, pendingSeedLineId]);

  const confirmGradeReassignment = useCallback((gradeCode, currentLineId) => {
    const normalized = Number(gradeCode);
    const conflictLines = schoolLines.filter(
      (line) => line.carpool_line_id !== currentLineId
        && parseCarpoolLineGrades(line).includes(normalized)
    );
    if (!conflictLines.length) return true;

    const gradeLabel = getGradeLabel(normalized, gradeLabelMap, { short: false });
    const lineLabels = conflictLines
      .map((line) => {
        const campus = campusById[line.campus_id];
        return campus ? `${campus.name} — ${line.name}` : line.name;
      })
      .join(', ');

    return window.confirm(
      `${gradeLabel} is already assigned to ${lineLabels}. `
      + `Assigning it here will turn it off on ${conflictLines.length > 1 ? 'those lines' : 'that line'}. Continue?`
    );
  }, [campusById, gradeLabelMap, schoolLines]);

  const confirmGradeSplitReassignment = useCallback((gradeSplitId, currentLineId) => {
    const normalized = Number(gradeSplitId);
    const conflictLines = schoolLines.filter(
      (line) => line.carpool_line_id !== currentLineId
        && parseCarpoolLineGradeSplitIds(line).includes(normalized)
    );
    if (!conflictLines.length) return true;

    const split = gradeSplitById[normalized];
    const splitLabel = split?.split_code || `Split #${normalized}`;
    const lineLabels = conflictLines
      .map((line) => {
        const campus = campusById[line.campus_id];
        return campus ? `${campus.name} — ${line.name}` : line.name;
      })
      .join(', ');

    return window.confirm(
      `${splitLabel} is already assigned to ${lineLabels}. `
      + `Assigning it here will turn it off on ${conflictLines.length > 1 ? 'those lines' : 'that line'}. Continue?`
    );
  }, [campusById, gradeSplitById, schoolLines]);

  const clearGradesFromOtherLines = useCallback(async (grades, currentLineId) => {
    const assignedSet = new Set((grades || []).map(Number));
    if (!assignedSet.size) return;

    const conflictLines = schoolLines.filter(
      (line) => line.carpool_line_id !== currentLineId
        && parseCarpoolLineGrades(line).some((grade) => assignedSet.has(grade))
    );

    await Promise.all(conflictLines.map((line) => {
      const remaining = parseCarpoolLineGrades(line).filter((grade) => !assignedSet.has(grade));
      return apiFetch(`/carpool-lines/${line.carpool_line_id}`, {
        method: 'PUT',
        body: JSON.stringify({ assigned_grades: remaining }),
      }, token);
    }));
  }, [schoolLines, token]);

  const clearGradeSplitsFromOtherLines = useCallback(async (gradeSplitIds, currentLineId) => {
    const assignedSet = new Set((gradeSplitIds || []).map(Number));
    if (!assignedSet.size) return;

    const conflictLines = schoolLines.filter(
      (line) => line.carpool_line_id !== currentLineId
        && parseCarpoolLineGradeSplitIds(line).some((splitId) => assignedSet.has(splitId))
    );

    await Promise.all(conflictLines.map((line) => {
      const remaining = parseCarpoolLineGradeSplitIds(line).filter((splitId) => !assignedSet.has(splitId));
      return apiFetch(`/carpool-lines/${line.carpool_line_id}`, {
        method: 'PUT',
        body: JSON.stringify({ assigned_grade_split_ids: remaining }),
      }, token);
    }));
  }, [schoolLines, token]);

  const handleCreatePickupLine = async ({
    campusId,
    name,
    lineType = 'parent',
    dismissalTime = '',
    assignedGrades = [],
    assignedGradeSplitIds = [],
    isDefault = false,
    notes = '',
  }) => {
    const selectedCampusId = Number.parseInt(campusId, 10);
    if (!Number.isInteger(selectedCampusId)) {
      setError('Select a campus before creating a pickup line.');
      return;
    }

    setCreatingLine(true);
    setError('');
    setSuccess('');

    try {
      const {
        assignedGrades: effectiveAssignedGrades,
        assignedGradeSplitIds: effectiveAssignedGradeSplitIds,
      } = getPickupLineAssignmentsForSave({
        groupingMode: studentGroupingMode,
        assignedGrades,
        assignedGradeSplitIds,
      });
      const response = await apiFetch('/carpool-lines', {
        method: 'POST',
        body: JSON.stringify({
          campus_id: selectedCampusId,
          name: name.trim(),
          line_type: lineType,
          dismissal_time: toApiTimeValue(dismissalTime),
          dismissal_time_overrides_defaults: Boolean(dismissalTime) && Boolean(lineForm.dismissal_time_overrides_defaults),
          active_duration_minutes: lineForm.active_duration_minutes === '' ? null : Number(lineForm.active_duration_minutes),
          assigned_grades: effectiveAssignedGrades,
          assigned_grade_split_ids: effectiveAssignedGradeSplitIds,
          is_default: Boolean(isDefault) || schoolLines.length === 0,
          is_active: true,
          geofenced_enabled: false,
          notes: notes.trim() || null,
        }),
      }, token);

      const newLine = response?.data || null;
      if (newLine?.carpool_line_id) {
        await Promise.all([
          clearGradesFromOtherLines(effectiveAssignedGrades, newLine.carpool_line_id),
          clearGradeSplitsFromOtherLines(effectiveAssignedGradeSplitIds, newLine.carpool_line_id),
        ]);
        setLineFormOpen(false);
        setSelectedLineId(newLine.carpool_line_id);
        setPendingSeedLineId(newLine.carpool_line_id);
        setSuccess('Pickup line created. Starter geofence is being prepared.');
        setLineForm(buildLineForm(campuses));
      }

      await load();
    } catch (err) {
      setError(err.message || 'Could not create a pickup line.');
    } finally {
      setCreatingLine(false);
    }
  };

  const handleOpenCreateLine = useCallback(() => {
    if (!campuses.length) {
      setCampusFormOpen(true);
      setPendingStarterSetup(true);
      setLineFormOpen(false);
      setError('Create a campus for this school before adding pickup lines.');
      return;
    }

    setLineForm((current) => {
      const nextForm = buildLineForm(campuses);
      return {
        ...nextForm,
        campus_id: current.campus_id && campuses.some((campus) => String(campus.campus_id) === String(current.campus_id))
          ? current.campus_id
          : nextForm.campus_id,
      };
    });
    setCampusFormOpen(false);
    setPendingStarterSetup(false);
    setLineFormOpen(true);
    setError('');
    setSuccess('');
  }, [campuses]);

  const handleLineSubmit = async (event) => {
    event.preventDefault();
    await handleCreatePickupLine({
      campusId: lineForm.campus_id,
      name: lineForm.name,
      lineType: lineForm.line_type,
      dismissalTime: lineForm.dismissal_time,
      assignedGrades: lineForm.assigned_grades,
      assignedGradeSplitIds: lineForm.assigned_grade_split_ids,
      isDefault: lineForm.is_default,
      notes: lineForm.notes,
    });
  };

  const handleSaveLineSettings = async () => {
    if (!selectedLine) return;

    setSavingLineSettings(true);
    setError('');
    setSuccess('');

    try {
      const {
        assignedGrades: effectiveAssignedGrades,
        assignedGradeSplitIds: effectiveAssignedGradeSplitIds,
      } = getPickupLineAssignmentsForSave({
        groupingMode: studentGroupingMode,
        assignedGrades: lineAssignedGrades,
        assignedGradeSplitIds: lineAssignedGradeSplitIds,
      });
      await Promise.all([
        clearGradesFromOtherLines(effectiveAssignedGrades, selectedLine.carpool_line_id),
        clearGradeSplitsFromOtherLines(effectiveAssignedGradeSplitIds, selectedLine.carpool_line_id),
      ]);

      await apiFetch(`/carpool-lines/${selectedLine.carpool_line_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          dismissal_time: toApiTimeValue(lineDismissalTime),
          // A line with no time of its own cannot be absolute.
          dismissal_time_overrides_defaults: Boolean(lineDismissalTime) && lineAbsoluteDismissalTime,
          // Blank clears the window and returns the line to always-live.
          active_duration_minutes: lineActiveDuration === '' ? null : Number(lineActiveDuration),
          assigned_grades: effectiveAssignedGrades,
          assigned_grade_split_ids: effectiveAssignedGradeSplitIds,
          is_default: lineIsDefault,
        }),
      }, token);

      setSuccess('Pickup line settings saved.');
      await load();
    } catch (err) {
      setError(err.message || 'Could not save pickup line settings.');
    } finally {
      setSavingLineSettings(false);
    }
  };

  const handleCampusSubmit = async (event) => {
    event.preventDefault();
    setCampusSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiFetch('/campuses', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          ...campusForm,
          state: campusForm.state.trim().toUpperCase(),
        }),
      }, token);

      const newCampus = response?.data || null;
      setCampusFormOpen(false);
      setSuccess('Campus created successfully.');
      await load();

      if (pendingStarterSetup && newCampus?.campus_id) {
        setPendingStarterSetup(false);
        setLineForm({
          campus_id: String(newCampus.campus_id),
          name: '',
          line_type: 'parent',
          dismissal_time: '',
          assigned_grades: [],
          assigned_grade_split_ids: [],
          is_default: false,
          notes: '',
        });
        setLineFormOpen(true);
      } else {
        setPendingStarterSetup(false);
      }
    } catch (err) {
      setError(err.message || 'Could not create campus.');
    } finally {
      setCampusSaving(false);
    }
  };

  const handleBuilderPlus = () => {
    handleOpenCreateLine();
  };

  const handleUndoPoint = () => {
    setDraftPoints((current) => current.slice(0, -1));
    setSuccess('');
  };

  const handleClearPoints = () => {
    setDraftPoints([]);
    setSuccess('');
  };

  const handleSaveZone = async () => {
    if (!selectedLine) return;
    if (draftPoints.length < 3) {
      setError('A geofence needs at least 3 boundary points.');
      return;
    }

    setSavingZone(true);
    setError('');
    setSuccess('');

    try {
      if (selectedZone) {
        await apiFetch(`/geofenced-zones/${selectedZone.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            carpool_line_id: selectedLine.carpool_line_id,
            points: draftPoints,
          }),
        }, token);
      } else {
        await apiFetch('/geofenced-zones', {
          method: 'POST',
          body: JSON.stringify({
            carpool_line_id: selectedLine.carpool_line_id,
            points: draftPoints,
          }),
        }, token);
      }

      if (!selectedLine.geofenced_enabled) {
        await apiFetch(`/carpool-lines/${selectedLine.carpool_line_id}/toggle-geofencing`, {
          method: 'PATCH',
          body: JSON.stringify({ geofenced_enabled: true }),
        }, token);
      }

      setSuccess('Geofence saved.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingZone(false);
    }
  };

  const content = (
    <div className="sap-page">
        {error ? <div className="sa-error sap-inline-error">{error}</div> : null}
        {success ? <div className="sa-success sap-inline-error">{success}</div> : null}

        {loading ? (
          <div className="sa-empty">Loading...</div>
        ) : (
          <div className="spz-shell">
            <section className="sap-parents-panel spz-list-panel">
              <div className="spz-line-list spz-line-card">
                {schoolLines.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">
                    {campuses.length === 0
                      ? 'No campuses are configured for this school yet.'
                      : 'No pickup lanes are configured for this school yet.'}
                  </div>
                ) : (
                  schoolLines.map((line) => {
                    const zone = zoneByLineId[line.carpool_line_id];
                    const campus = campusById[line.campus_id];
                    const status = getLineStatus(line, zone);

                    return (
                      <button
                        key={line.carpool_line_id}
                        className={`spz-line-row ${selectedLine?.carpool_line_id === line.carpool_line_id ? 'active' : ''}`}
                        onClick={() => setSelectedLineId(line.carpool_line_id)}
                      >
                        <div className="spz-line-copy">
                          <span className="spz-line-name">
                            {campus ? `${campus.name} — ${line.name}` : line.name}
                          </span>
                          <span className={`spz-status ${status.tone}`}>{status.label}</span>
                        </div>
                        <span className="sap-directory-arrow">→</span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="sap-section-card spz-builder-card">
                <div className="spz-builder-head">
                  <div>
                    <div className="spz-builder-title">Zone Builder</div>
                    <p>Define the area where parent arrivals are automatically detected</p>
                  </div>
                  <button
                    className="spz-builder-plus"
                    onClick={handleBuilderPlus}
                    title={schoolLines.length ? 'Create or focus the next zone needing setup' : 'Create a pickup line and start the first zone'}
                    disabled={creatingLine}
                  >
                    {creatingLine ? '…' : '+'}
                  </button>
                </div>
                {campusFormOpen && campuses.length === 0 ? (
                  <form className="sa-form-grid" onSubmit={handleCampusSubmit}>
                    <div className="sa-field full-width">
                      <label>Campus Name *</label>
                      <input
                        value={campusForm.name}
                        onChange={(event) => setCampusForm((current) => ({ ...current, name: event.target.value }))}
                        required
                      />
                    </div>
                    <div className="sa-field">
                      <label>Campus Type</label>
                      <select
                        value={campusForm.type}
                        onChange={(event) => setCampusForm((current) => ({ ...current, type: event.target.value }))}
                      >
                        {CAMPUS_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </div>
                    <div className="sa-field">
                      <label>Time Zone *</label>
                      <select
                        value={campusForm.time_zone}
                        onChange={(event) => setCampusForm((current) => ({ ...current, time_zone: event.target.value }))}
                        required
                      >
                        {CAMPUS_TIME_ZONES.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
                      </select>
                    </div>
                    <div className="sa-field full-width">
                      <label>Street Address *</label>
                      <input
                        value={campusForm.street_address}
                        onChange={(event) => setCampusForm((current) => ({ ...current, street_address: event.target.value }))}
                        required
                      />
                    </div>
                    <div className="sa-field">
                      <label>City *</label>
                      <input
                        value={campusForm.city}
                        onChange={(event) => setCampusForm((current) => ({ ...current, city: event.target.value }))}
                        required
                      />
                    </div>
                    <div className="sa-field">
                      <label>State *</label>
                      <input
                        value={campusForm.state}
                        maxLength={2}
                        onChange={(event) => setCampusForm((current) => ({ ...current, state: event.target.value.toUpperCase() }))}
                        required
                      />
                    </div>
                    <div className="sa-field">
                      <label>ZIP *</label>
                      <input
                        value={campusForm.zip_code}
                        onChange={(event) => setCampusForm((current) => ({ ...current, zip_code: event.target.value }))}
                        required
                      />
                    </div>
                    <div className="sa-field">
                      <label>Main Phone</label>
                      <input
                        value={campusForm.main_phone}
                        onChange={(event) => setCampusForm((current) => ({ ...current, main_phone: event.target.value }))}
                      />
                    </div>
                    <div className="sa-field full-width">
                      <label>Main Email</label>
                      <input
                        type="email"
                        value={campusForm.main_email}
                        onChange={(event) => setCampusForm((current) => ({ ...current, main_email: event.target.value }))}
                      />
                    </div>
                    <div className="sa-btn-row">
                      <button type="submit" className="sa-btn sa-btn-primary" disabled={campusSaving}>
                        {campusSaving ? 'Creating...' : 'Create Campus'}
                      </button>
                      <button
                        type="button"
                        className="sa-btn sa-btn-secondary"
                        onClick={() => {
                          setCampusFormOpen(false);
                          setPendingStarterSetup(false);
                          setError('');
                        }}
                        disabled={campusSaving}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : lineFormOpen ? (
                  <form className="sa-form-grid" onSubmit={handleLineSubmit}>
                    <div className="sa-field">
                      <label>Campus *</label>
                      <select
                        value={lineForm.campus_id}
                        onChange={(event) => setLineForm((current) => ({ ...current, campus_id: event.target.value }))}
                        required
                      >
                        {campuses.map((campus) => (
                          <option key={campus.campus_id} value={campus.campus_id}>
                            {campus.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sa-field">
                      <label>Line Type *</label>
                      <select
                        value={lineForm.line_type}
                        onChange={(event) => setLineForm((current) => ({ ...current, line_type: event.target.value }))}
                        required
                      >
                        {CARPOOL_LINE_TYPES.map((type) => (
                          <option key={type} value={type}>{formatLineType(type)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sa-field full-width">
                      <label>Pickup Line Name *</label>
                      <input
                        value={lineForm.name}
                        onChange={(event) => setLineForm((current) => ({ ...current, name: event.target.value }))}
                        placeholder="Main Pickup Line"
                        required
                      />
                    </div>
                    <div className="sa-field">
                      <label>Dismissal Time</label>
                      <input
                        type="time"
                        value={lineForm.dismissal_time}
                        onChange={(event) => setLineForm((current) => ({ ...current, dismissal_time: event.target.value }))}
                      />
                    </div>
                    <div className="sa-field">
                      <label>Ignore Dismissal Defaults</label>
                      <label className="spz-checkbox-row">
                        <input
                          type="checkbox"
                          checked={lineForm.dismissal_time_overrides_defaults}
                          disabled={!lineForm.dismissal_time}
                          onChange={(event) => setLineForm((current) => ({
                            ...current,
                            dismissal_time_overrides_defaults: event.target.checked,
                          }))}
                        />
                        <span>Always use this time, even on early-dismissal days</span>
                      </label>
                    </div>
                    <div className="sa-field">
                      <label>Default Line</label>
                      <label className="spz-checkbox-row">
                        <input
                          type="checkbox"
                          checked={lineForm.is_default}
                          onChange={(event) => setLineForm((current) => ({ ...current, is_default: event.target.checked }))}
                        />
                        <span>Main fallback line</span>
                      </label>
                    </div>
                    {showGradeAssignments ? (
                      <div className="sa-field full-width">
                        <label>Grades Using This Line</label>
                        <div className="spz-grade-grid">
                          {gradeOptions.map((grade) => (
                            <label key={grade.gradeCode} className="spz-grade-check">
                              <input
                                type="checkbox"
                                checked={(lineForm.assigned_grades || []).map(Number).includes(grade.gradeCode)}
                                onChange={() => {
                                  const alreadyOn = (lineForm.assigned_grades || []).map(Number).includes(grade.gradeCode);
                                  if (!alreadyOn && !confirmGradeReassignment(grade.gradeCode, null)) return;
                                  setLineForm((current) => ({
                                    ...current,
                                    assigned_grades: toggleGradeSelection(current.assigned_grades, grade.gradeCode),
                                  }));
                                }}
                              />
                              <span>{grade.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {showGradeSplitAssignments ? (
                      <div className="sa-field full-width">
                        <label>Splits Using This Line</label>
                        {gradeSplitOptions.length ? (
                          <div className="spz-grade-grid">
                            {gradeSplitOptions.map((split) => (
                              <label key={split.splitId} className="spz-grade-check">
                                <input
                                  type="checkbox"
                                  checked={(lineForm.assigned_grade_split_ids || []).map(Number).includes(split.splitId)}
                                  onChange={() => {
                                    const alreadyOn = (lineForm.assigned_grade_split_ids || []).map(Number).includes(split.splitId);
                                    if (!alreadyOn && !confirmGradeSplitReassignment(split.splitId, null)) return;
                                    setLineForm((current) => ({
                                      ...current,
                                      assigned_grade_split_ids: toggleGradeSelection(current.assigned_grade_split_ids, split.splitId),
                                    }));
                                  }}
                                />
                                <span>{split.label}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="sa-empty sap-compact-empty">No active splits are configured for this school.</div>
                        )}
                      </div>
                    ) : null}
                    <div className="sa-field full-width">
                      <label>Notes</label>
                      <textarea
                        value={lineForm.notes}
                        onChange={(event) => setLineForm((current) => ({ ...current, notes: event.target.value }))}
                        rows={3}
                        placeholder="Optional notes for staff"
                      />
                    </div>
                    <div className="sa-btn-row">
                      <button type="submit" className="sa-btn sa-btn-primary" disabled={creatingLine || !lineForm.name.trim() || !lineForm.campus_id}>
                        {creatingLine ? 'Creating...' : 'Create Pickup Line'}
                      </button>
                      <button
                        type="button"
                        className="sa-btn sa-btn-secondary"
                        onClick={() => {
                          setLineFormOpen(false);
                          setError('');
                        }}
                        disabled={creatingLine}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <ZonePreview points={selectedPoints} />
                )}
              </div>
            </section>

            <section className="sap-parents-panel spz-detail-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>{selectedCampus ? `${selectedCampus.name} — ${selectedLine?.name || 'Zone'}` : selectedLine?.name || 'Pickup Zone'}</h2>
                  <p className={`spz-status ${selectedStatus.tone}`}>{selectedStatus.label}</p>
                </div>
              </div>

              {selectedLine ? (
                <>
                  <div ref={editorCardRef} className="spz-map-editor-card">
                    <GoogleZoneEditor
                      apiKey={GOOGLE_MAPS_API_KEY}
                      campus={selectedCampus}
                      points={draftPoints}
                      onPointsChange={setDraftPoints}
                    />
                    <div className="spz-map-editor-toolbar">
                      <span>{draftPoints.length} boundary point{draftPoints.length === 1 ? '' : 's'}</span>
                      <button className="sa-btn sa-btn-secondary" onClick={handleUndoPoint} disabled={savingZone || draftPoints.length === 0}>
                        Undo Point
                      </button>
                      <button className="sa-btn sa-btn-secondary" onClick={handleClearPoints} disabled={savingZone || draftPoints.length === 0}>
                        Clear
                      </button>
                      <button className="sa-btn sa-btn-primary" onClick={handleSaveZone} disabled={savingZone || draftPoints.length < 3}>
                        {savingZone ? 'Saving...' : 'Save Geofence'}
                      </button>
                    </div>
                  </div>
                  <div className="sap-section-card spz-info-card">
                    <InfoRow label="Campus" value={selectedCampus?.name} />
                    <InfoRow label="Line Type" value={formatLineType(selectedLine.line_type)} />
                    <InfoRow label="Default Line" value={isDefaultCarpoolLine(selectedLine) ? 'Yes' : 'No'} />
                    {showGradeAssignments ? (
                      <InfoRow label="Grade Fallbacks" value={formatAssignedGrades(selectedLine, gradeLabelMap)} />
                    ) : null}
                    {showGradeSplitAssignments ? (
                      <InfoRow
                        label="Split Fallbacks"
                        value={formatAssignedGradeSplits(
                          selectedLine,
                          gradeSplitById,
                          gradeLabelMap,
                          studentGroupingMode === 'grades_and_splits'
                        )}
                      />
                    ) : null}
                    <InfoRow
                      label="Dismissal Time"
                      value={selectedLine.dismissal_time
                        ? formatPickupTime(selectedLine.dismissal_time)
                        : `Default (${formatPickupTime(effectiveDismissalTime)})`}
                    />
                    <div className="sa-field">
                      <label>Line Dismissal Time Override</label>
                      <input
                        type="time"
                        value={lineDismissalTime}
                        onChange={(event) => setLineDismissalTime(event.target.value)}
                        disabled={savingLineSettings}
                      />
                      <label className="spz-checkbox-row">
                        <input
                          type="checkbox"
                          checked={lineAbsoluteDismissalTime}
                          disabled={savingLineSettings || !lineDismissalTime}
                          onChange={(event) => setLineAbsoluteDismissalTime(event.target.checked)}
                        />
                        <span>Always use this time, even on early-dismissal days</span>
                      </label>
                      <p className="spz-hint">
                        {lineDismissalTime && lineAbsoluteDismissalTime
                          ? 'Weekday and school dismissal defaults are ignored for this line.'
                          : 'A weekday default (such as an early Friday) takes precedence over this time.'}
                      </p>
                    </div>

                    <div className="sa-field">
                      <label>Pickup Window (minutes)</label>
                      <input
                        type="number"
                        min="1"
                        max="1440"
                        placeholder="Live all day"
                        value={lineActiveDuration}
                        onChange={(event) => setLineActiveDuration(event.target.value)}
                        disabled={savingLineSettings || !lineDismissalTime}
                      />
                      <p className="spz-hint">
                        {lineDismissalTime
                          ? `${describeWindow({
                            dismissal_time: lineDismissalTime,
                            active_duration_minutes: lineActiveDuration === '' ? null : Number(lineActiveDuration),
                          })}. Outside this window the line's pickup zone is ignored.`
                          : 'Set a dismissal time first to limit when this zone is live.'}
                      </p>

                      {scheduleConflicts.length > 0 && (
                        <div className="spz-warning">
                          <strong>Runs at the same time as {scheduleConflicts.map((line) => line.name).join(', ')}.</strong>
                          {' '}
                          If those lines cover the same area, one parent arriving raises a
                          pickup event on every one of them — students get called repeatedly
                          and the board fills with duplicates. Give each line its own pickup
                          window, or make sure their zones do not overlap.
                          <div style={{ marginTop: 6, fontSize: 12 }}>
                            {scheduleConflicts.map((line) => (
                              <div key={line.carpool_line_id}>{line.name}: {describeWindow(line)}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="sa-field">
                      <label>Default Line</label>
                      <label className="spz-checkbox-row">
                        <input
                          type="checkbox"
                          checked={lineIsDefault}
                          onChange={(event) => setLineIsDefault(event.target.checked)}
                          disabled={savingLineSettings}
                        />
                        <span>Main fallback line</span>
                      </label>
                    </div>
                    {showGradeAssignments ? (
                      <div className="sa-field full-width">
                        <label>Grades Using This Line</label>
                        <div className="spz-grade-grid">
                          {gradeOptions.map((grade) => (
                            <label key={grade.gradeCode} className="spz-grade-check">
                              <input
                                type="checkbox"
                                checked={lineAssignedGrades.map(Number).includes(grade.gradeCode)}
                                onChange={() => {
                                  const alreadyOn = lineAssignedGrades.map(Number).includes(grade.gradeCode);
                                  if (!alreadyOn && !confirmGradeReassignment(grade.gradeCode, selectedLine.carpool_line_id)) return;
                                  setLineAssignedGrades((current) => toggleGradeSelection(current, grade.gradeCode));
                                }}
                                disabled={savingLineSettings}
                              />
                              <span>{grade.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {showGradeSplitAssignments ? (
                      <div className="sa-field full-width">
                        <label>Splits Using This Line</label>
                        {gradeSplitOptions.length ? (
                          <div className="spz-grade-grid">
                            {gradeSplitOptions.map((split) => (
                              <label key={split.splitId} className="spz-grade-check">
                                <input
                                  type="checkbox"
                                  checked={lineAssignedGradeSplitIds.map(Number).includes(split.splitId)}
                                  onChange={() => {
                                    const alreadyOn = lineAssignedGradeSplitIds.map(Number).includes(split.splitId);
                                    if (!alreadyOn && !confirmGradeSplitReassignment(split.splitId, selectedLine.carpool_line_id)) return;
                                    setLineAssignedGradeSplitIds((current) => toggleGradeSelection(current, split.splitId));
                                  }}
                                  disabled={savingLineSettings}
                                />
                                <span>{split.label}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="sa-empty sap-compact-empty">No active splits are configured for this school.</div>
                        )}
                      </div>
                    ) : null}
                    <div className="sa-btn-row">
                      <button
                        type="button"
                        className="sa-btn sa-btn-primary"
                        onClick={handleSaveLineSettings}
                        disabled={savingLineSettings}
                      >
                        {savingLineSettings ? 'Saving...' : 'Save Settings'}
                      </button>
                      <button
                        type="button"
                        className="sa-btn sa-btn-secondary"
                        onClick={() => { setLineDismissalTime(''); setLineAbsoluteDismissalTime(false); }}
                        disabled={savingLineSettings || !lineDismissalTime}
                      >
                        Use Default
                      </button>
                    </div>
                    <InfoRow label="Detection" value={selectedLine.geofenced_enabled ? 'Automatic geofence detection' : 'Manual only'} />
                    <InfoRow label="Boundary Points" value={draftPoints.length ? `${draftPoints.length} pending points` : 'No polygon saved'} />
                    <InfoRow label="Notes" value={selectedLine.notes} />
                  </div>
                </>
              ) : (
                <div className="sa-empty sap-compact-empty">Select a pickup line to view its zone.</div>
              )}
            </section>

            <section className="sap-parents-panel spz-activity-panel">
              <div className="sap-panel-heading">
                <div>
                  <h2>Zone Activity</h2>
                  <p>Track recent changes and detection events</p>
                </div>
              </div>

              <div className="spz-activity-list">
                {activityItems.length === 0 ? (
                  <div className="sa-empty sap-compact-empty">No zone updates have been recorded for this school yet.</div>
                ) : (
                  activityItems.map((item) => (
                    <div key={item.id} className="spz-activity-item">
                      <div className="spz-activity-title">{item.title}</div>
                      <div className="spz-activity-line">{item.lineName}</div>
                      <div className="spz-activity-detail">{item.detail}</div>
                      <div className="spz-activity-time">{formatTimestamp(item.timestamp)}</div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>
  );

  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="pickup-zones"
      breadcrumb="Pickup Zones"
    >
      {content}
    </SchoolLayout>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="sap-info-row">
      <span className="sap-info-label">{label}</span>
      <span className="sap-info-value">{value || '—'}</span>
    </div>
  );
}
